import {Injectable} from "@angular/core";
import {GridLayoutService} from "@irb-common/interfaces/grid-layout.service";
import {ComPrOrdersComponent} from "@irb/irb-editors/editor-portfolio-result/com-pr-orders/com-pr-orders.component";
import {UtilityService} from "@irb-common/services/utility.service";
import {NumberTextBoxEditorComponent} from '@irb-common/components/grid/editors/number-text-box-editor.component';
import {NumberTextBoxRendererComponent} from "@irb-common/components/grid/renderers/number-text-box.cell.component";
import {PrApplicationService, PrOrdersService} from "../services";
import {DecimalPipe} from '@angular/common';
import {DropdownFilterRendererComponent} from '@irb-common/components/grid/renderers/dropdown-filter.cell.component';
import {IDropdownItem} from '@irb-common/models/dropdown.model';
import {CallToActionCellComponent} from '@irb-common/components/grid/renderers/calltoaction.cell.component';
import {ComPrOrdersAccountGroupcellComponent} from "@irb/irb-editors/editor-portfolio-result/com-pr-orders/com-pr-orders-account-groupcell/com-pr-orders-account-groupcell.component";

@Injectable({
    providedIn: "root"
})
export class ComPrOrdersGridLayoutService implements GridLayoutService<ComPrOrdersComponent> {
    sizeFor: any;

    constructor(private util: UtilityService, private pros: PrOrdersService, private dp: DecimalPipe, private pras: PrApplicationService) {
        this.sizeFor = {
            "accountNum": 160,
            "txnType": 150,
            "accountLocationType": 115,
            "washSell": 95
        };
    }

    getFrameworkComponents() {
        return {
            "DropdownFilterRendererComponent": DropdownFilterRendererComponent,
            "NumberTextBoxEditorComponent": NumberTextBoxEditorComponent,
            "NumberTextBoxRendererComponent": NumberTextBoxRendererComponent,
            "CallToActionCellComponent": CallToActionCellComponent,
            "ComPrOrdersAccountGroupcellComponent": ComPrOrdersAccountGroupcellComponent
        };
    }

    getColumnDefs(parent: ComPrOrdersComponent) {
        return [{
            headerName: "",
            marryChildren:true,
            suppressMovable: true,
            children: [
                {
                    headerName: "Acct#",
                    suppressMovable: true,
                    field: "accountNum",
                    comparator: (valueA: number, valueB: number, nodeA: any, nodeB: any) => {
                        return this.preventLotSort(valueA, valueB, nodeA, nodeB, this.util.sortString.bind(parent));
                    },
                    showRowGroup: true,
                    cellRenderer: 'agGroupCellRenderer',
                    cellRendererParams: {
                        suppressCount: true,
                        suppressDoubleClickExpand: true,
                        innerRenderer:"ComPrOrdersAccountGroupcellComponent",
                        innerRendererParams:{parentComponent: parent},
                        suppressEnterExpand: true,
                    },
                    minWidth: this.sizeFor.accountNum,
                    flex: 2,
                    valueFormatter: (params: any) => {
                        const isLot: boolean = params.data.parentId ? true : false;
                        return (isLot) ? "" : params.data.accountNum;
                    }
                },
                {
                    headerName: "Tax",
                    suppressMovable: true,
                    headerComponentParams: {
                        irbColInfo: this.getTaxColInfo()
                    },
                    field: "accountLocationType",
                    maxWidth: this.sizeFor.accountLocationType
                },
                {
                    headerName: "Symbol",
                    suppressMovable: true,
                    field: "secSymbol",
                    flex: 2,
                    headerComponentParams: {
                        irbColInfo: `A warning icon in this column means that symbol has information to review. Select the View Explanations button for more.`
                    },
                    cellRenderer: 'CallToActionCellComponent',
                    cellRendererParams: {
                        getTemplate: (params: any) => parent.getSecurityDescription(params),
                        calltoactionUsesHover: true,
                        calltoactionMaxWidth: 'none',
                        valueFormatter: (params: any) => {
                            const isLot: boolean = params.data.parentId ? true : false;
                            if (isLot) {
                                return params.data.lotPurchaseDate;
                            } else {
                                return (params.data?.secExclusionType) ? "* " + params.value : params.value;
                            }
                        },
                        calltoactionclass: (params: any) => {
                            if (params.data.secType === 'GROUP' && params.data.rebalCompSecurityDTOList) {
                                return 'link-to-click';
                            }
                            return 'no-link-class'
                        },
                        calltoactionClick: (params: any) => {
                            parent.showGroupComponents(params);
                        },
                        showWarnIcon: (_params: any) => this.pras.getSecurityTradeActionNote(parent.data,_params.data?.secSymbol).length > 0,
                        getWarnIconTemplate: (_params: any) => parent.getSecurityTradeActionNoteTemplate(this.pras.getSecurityTradeActionNote(parent.data,_params.data?.secSymbol))       
                    }
                }
            ]
        },
            {
                headerName: "Current position",
                marryChildren:true,
                suppressMovable: true,
                children: [
                    {
                        headerName: "Mkt Val $",
                        suppressMovable: true,
                        field: "roundedMktValue",
                        flex: 2,
                        cellRenderer: 'cellRendererFormatter',
                        cellRendererParams: {type: 'currency', decimalPosition: '2'},
                        comparator: (valueA: number, valueB: number, nodeA: any, nodeB: any) => {
                            return this.preventLotSort(valueA, valueB, nodeA, nodeB, (a: number, b: number) => a - b);
                        }
                    },
                    {
                        headerName: "Shares",
                        suppressMovable: true,
                        field: "currentShares",
                        flex: 2,
                        cellRenderer: 'cellRendererFormatter',
                        cellRendererParams: (params: any) => ({
                            type: 'currency',
                            displaySymbol: ' ',
                            decimalPosition: '4',
                            isNotApplicable: ['GROUP', 'OTHER'].indexOf(params.data?.secType) !== -1
                        }),
                        comparator: (valueA: number, valueB: number, nodeA: any, nodeB: any) => {
                            return this.preventLotSort(valueA, valueB, nodeA, nodeB, this.sortShares);
                        }
                    },
                    {
                        headerName: "Cost $",
                        suppressMovable: true,
                        field: "costValue",
                        flex: 2,
                        cellRenderer: 'cellRendererFormatter',
                        cellRendererParams: {type: 'currency', decimalPosition: '2'},
                        comparator: (valueA: number, valueB: number, nodeA: any, nodeB: any) => {
                            return this.preventLotSort(valueA, valueB, nodeA, nodeB, (a: number, b: number) => a - b);
                        }
                    },
                    {
                        headerName: "Unr G/L $",
                        suppressMovable: true,
                        field: "unrealzdGainOrLoss",
                        flex: 2,
                        cellRenderer: 'cellRendererFormatter',
                        headerComponentParams: {
                            irbColInfo: this.unrColInfo()
                        },
                        cellRendererParams: {
                            type: 'currency',
                            displaySymbol: ' ',
                            decimalPosition: '0'
                        },
                        comparator: (valueA: number, valueB: number, nodeA: any, nodeB: any) => {
                            return this.preventLotSort(valueA, valueB, nodeA, nodeB, (a: number, b: number) => a - b);
                        }
                    },
                    {
                        headerName: "G/L %",
                        suppressMovable: true,
                        field: "unrealzdGainLossPercntg",
                        flex: 2,
                        cellRenderer: 'cellRendererFormatter',
                        cellRendererParams: {
                            type: 'currency',
                            displaySymbol: ' ',
                            decimalPosition: '0'
                        },
                        comparator: (valueA: number, valueB: number, nodeA: any, nodeB: any) => {
                            return this.preventLotSort(valueA, valueB, nodeA, nodeB, (a: number, b: number) => a - b);
                        }
                    }
                ]
            },
            {
                headerName: "Orders",
                headerGroupComponent: 'customHeaderGroup',
                headerGroupComponentParams: {
                    irbColInfo: this.ordersGroupColInfo()
                },
                headerClass: "adjustment",
                marryChildren:true,
                suppressMovable: true,
                children: [
                    {
                        headerName: "Trans Type",
                        suppressMovable: true,
                        field: "txnType",
                        minWidth: this.sizeFor.txnType,
                        flex: 2,
                        headerClass: "adjustment",
                        cellClass: "adjustment dropdownFilterCell",
                        cellClassRules: {
                            'userEdit': (params: any) => params.data?.txnType !== params.data?.iRblTxnType
                        },
                        cellRenderer: 'DropdownFilterRendererComponent',
                        cellRendererParams: (params: any) => {
                            return {
                                defaultValue: parent.getTransactionType(params.data?.txnType),
                                values: parent.getTransactionTypes(params),
                                disabled: !parent.canEditTransactionType(params) || parent.isAutomated,
                                readonly: true,
                                placeholder: "",
                                onChange: (params: any, item: IDropdownItem) => {
                                    params.data.txnType = item.value;
                                    if (params.node && params.node.data && params.node.data.txnType) {
                                        params.node.data.txnType = item.value;
                                    }
                                    parent.onCellEditingStopped(params);
                                }
                            };
                        },
                        comparator: (valueA: number, valueB: number, nodeA: any, nodeB: any) => {
                            return this.preventLotSort(valueA, valueB, nodeA, nodeB, this.util.sortString.bind(parent));
                        }
                    },
                    {
                        headerName: "Amt",
                        suppressMovable: true,
                        field: "txnAmt",
                        flex: 2,
                        headerClass: "adjustment",
                        cellClass: ["adjustment",'numberTextBoxCell'],
                        // headerComponentParams: {
                        //     irbColInfo: this.orderAmountColInfo()
                        // },
                        cellClassRules: {
                            'userEdit': (params: any) => params.data?.txnAmt !== params.data?.roundedIRblTxnAmt,
                            'isDisabled': (params: any) => (!parent.canEditOrderAmount(params) || parent.isAutomated)
                        },
                        cellRenderer: "NumberTextBoxRendererComponent",
                        cellRendererParams: (params: any) => {
                            return {
                                min: 0,
                                places: 2,
                                isCurrency: true,
                                isNegative: this.pros.getSellType(params.data?.txnType),
                                isBold: /*params.data?.txnCurrency === "DOLLARS"*/ !params.data.txnSharesLocked  && params.data.txnType !== 'SELL ALL',
                                disabled: !parent.canEditOrderAmount(params) || parent.isAutomated,
                                onChange: (params: any, c: any) => {
                                    if (params && params.value != c.value && c.isValid) {
                                        params.value = parseFloat(c.value);
                                        setTimeout(() => parent.onCellEditingStopped(params));
                                    }
                                },
                            };
                        },
                        comparator: (valueA: number, valueB: number, nodeA: any, nodeB: any) => {
                            return this.preventLotSort(valueA, valueB, nodeA, nodeB, this.sortTxnAmt.bind(parent));
                        }
                    },
                    {
                        headerName: "Shares",
                        suppressMovable: true,
                        field: "txnShares",
                        flex: 2,
                        headerClass: "adjustment",
                        cellClass: ["adjustment", 'numberTextBoxCell'],
                        // headerComponentParams: {
                        //     irbColInfo: this.estimatedSharesColInfo()
                        // },
                        cellClassRules: {
                            'userEdit': (params: any) => {
                                if(params.data) {
                                    const a = params.data.txnAmt !== params.data.roundedIRblTxnAmt;
                                    const isLot: boolean = params.data.parentId ? true : false;

                                    if(a) {
                                        const p = (isLot) ? this.pros.getParentItem(parent.unpackedSecRebalDTOList, params.data.parentId) : params.data;
                                        const b = (['EQ', 'MF', 'OPTION'].indexOf(p.secType) !== -1);
                                        return a && b;
                                    }
                                }

                                return false;
                            },
                            'isDisabled': (params: any) => (!parent.canEditOrderShares(params) || parent.isAutomated)
                        },
                        cellRenderer: "NumberTextBoxRendererComponent",
                        cellRendererParams: (params: any) => {
                            const isLot: boolean = params.data.parentId ? true : false;
                            return {
                                min: 0,
                                places: this.getSharesPlaces(parent, params),
                                displayPlaces: 4,
                                invalidMessage: this.getSharesInvalidMessage(parent, params),
                                isNegative: this.pros.getSellType(params.data?.txnType),
                                isBold: /*params.data.txnCurrency === "SHARES"*/ params.data.txnSharesLocked || params.data.txnType === 'SELL ALL',
                                isNotApplicable: ['EQ', 'MF', 'OPTION', 'EXTERNAL', 'BOND'].indexOf(params.data?.secType) === -1 && !isLot,
                                disabled: !parent.canEditOrderShares(params) || parent.isAutomated,
                                onChange: (params: any, c: any) => {
                                    if (params && params.value != c.value && c.isValid) {
                                        params.value = parseFloat(c.value);
                                        setTimeout(() => parent.onCellEditingStopped(params));
                                    }
                                },
                            };
                        },
                        comparator: (valueA: number, valueB: number, nodeA: any, nodeB: any) => {
                            return this.preventLotSort(valueA, valueB, nodeA, nodeB, this.sortTxnShares.bind(parent));
                        }
                    },
                    {
                        headerName: "Amt Type",
                        suppressMovable: true,
                        field: "txnSharesLocked",
                        flex: 2,
                        headerClass: "adjustment",
                        cellClass: ["adjustment"],
                        cellRendererSelector: this.amtTypeCellFormatter.bind(this),
                        sortable: false
                    }
                ]
            },
            {
                headerName: "Adjusted position (est)",
                marryChildren:true,
                suppressMovable: true,
                children: [
                    {
                        headerName: "Mkt Val $",
                        suppressMovable: true,
                        field: "afterRebalMktVal",
                        flex: 2,
                        cellRenderer: 'cellRendererFormatter',
                        cellRendererParams: {type: 'currency', decimalPosition: '2'},
                        comparator: (valueA: number, valueB: number, nodeA: any, nodeB: any) => {
                            return this.preventLotSort(valueA, valueB, nodeA, nodeB, (a: number, b: number) => a - b);
                        }
                    },
                    {
                        headerName: "Shares",
                        suppressMovable: true,
                        field: "afterRebalShares",
                        flex: 2,
                        cellRenderer: 'cellRendererFormatter',
                        cellRendererParams: (params: any) => ({
                            type: 'currency',
                            displaySymbol: ' ',
                            decimalPosition: '4',
                            isNotApplicable: ['GROUP', 'OTHER'].indexOf(params.data?.secType) !== -1
                        }),
                        comparator: (valueA: number, valueB: number, nodeA: any, nodeB: any) => {
                            return this.preventLotSort(valueA, valueB, nodeA, nodeB, this.sortShares);
                        }
                    },
                    {
                        headerName: "Real G/L $",
                        suppressMovable: true,
                        field: "realzdGainLoss",
                        flex: 2,
                        cellRenderer: 'cellRendererFormatter',
                        cellRendererParams: (params: any) => {
                            const sellTerm = params.data?.sellTerm;
                            const rendererParams = {
                                type: 'currency',
                                displaySymbol: ' ',
                                decimalPosition: '0'
                            };
                            return sellTerm ? {...rendererParams, appendToEnd: ' ' + sellTerm} : rendererParams;
                        },
                        comparator: (valueA: number, valueB: number, nodeA: any, nodeB: any) => {
                            return this.preventLotSort(valueA, valueB, nodeA, nodeB, (a: number, b: number) => a - b);
                        }
                    },
                    {
                        headerName: "Penalty",
                        suppressMovable: true,
                        field: "penaltyAmt",
                        flex: 2,
                        cellRenderer: 'cellRendererFormatter',
                        cellRendererParams: {type: 'currency'},
                        comparator: (valueA: number, valueB: number, nodeA: any, nodeB: any) => {
                            return this.preventLotSort(valueA, valueB, nodeA, nodeB, (a: number, b: number) => a - b);
                        }
                    },
                    {
                        headerName: "W",
                        suppressMovable: true,
                        field: "washSell",
                        headerComponentParams: {
                            irbColInfo: this.washSaleColInfo(),
                            irbColPosition: 'left'
                        },
                        cellRendererSelector: this.washCellFormatter.bind(this),
                        maxWidth: this.sizeFor.washSell,
                        comparator: (valueA: number, valueB: number, nodeA: any, nodeB: any) => {
                            return this.preventLotSort(valueA, valueB, nodeA, nodeB, (a: number, b: number) => a - b);
                        }
                    }
                ]
            }];
    }
    amtTypeCellFormatter(params: any) {
        return {
            component: "cellRendererFormatter",
            params: {
                value: params?.data?.txnType === 'NONE' ? '' : (params?.data?.txnSharesLocked || params?.data?.txnType ==="SELL ALL") ? "S" : "D"
            }
        };
    }
    washCellFormatter(params: any) {
        return {
            component: "cellRendererFormatter",
            params: {
                value: params.data
                && (params.data.txnType === 'BUY' && params.data.washSell
                || params.data.txnType.includes('SELL') && params.data.washSaleLotSoldAtLoss
                ) ? "W" : "", type: 'errorMsg'
            }
        };
    }

    washSaleColInfo() {
        return `
          <p>This column will show a red <span class='red'>W</span> when you have a wash sale warning. This happens when the iRebal order blotter shows a sale done at a loss for the same security in a taxable account in this portfolio within the last 30 days. If you purchase the security, you may nullify a loss by violating the wash sale.</p>
          `
    }

    getTaxColInfo() {
        return `
        <p>Deferred</p>
        <p>Roth </p>
        <p>Taxable</p>
        `
    }

    ordersGroupColInfo() {
        return `
            <p>Order Amount</p>

            <p>This is the potential order amount iRebal has presented.</p>
            <p>If the order amount is shown in bold, iRebal will continue to pull the most recent quote and round your order quantity based on price of the security and rounding parameters until the time of order submission.</p>
            <p>All other orders will be submitted based on the share quantity in the Estimated Order Share column.</p>
            <hr>
            
            <p>Estimated Order Shares</p>
            
            <p>This is the estimated amount of shares for your order, based on order amounts and price of the security at the time of rebalance.</p>
            
            <p>This does not factor in rounding parameters from global settings.</p>
            
            <p>All option orders and user-edited trades in shares will lock in the share quantity and be shown in bold.</p>
            
            <p>All other orders will continue to pull the most recent quote and round your order quantity based on price of the security and rounding parameters until the time of order submission.</p>
            <hr>
            
            <p>Amount Type</p>
            
            <p>"S" indicates shares.<br>
            "D" indicates dollars.</p>
        `;
    }
    // orderAmountColInfo() {
    //     return `
    //       <p>This is the potential order amount iRebal has presented. If the order amount is shown in bold, iRebal will continue to pull the most recent quote and round your order quantity based on price of the security and rounding parameters until the time of order submission. All other orders will be submitted based on the share quantity in the Estimated Order Share column.</p>
    //       `
    // }
    //
    // estimatedSharesColInfo() {
    //     return `
    //       <p>This is the estimated amount of shares for your order, based on order amounts and price of the security at the time of rebalance. This does not factor in rounding parameters from global settings. All option orders and user-edited trades in shares will lock in the share quantity and be shown in bold. All other orders will continue to pull the most recent quote and round your order quantity based on price of the security and rounding parameters until the time of order submission.</p>
    //       `
    // }

    unrColInfo() {
        return `
          <p>This is the estimated unrealized gain loss for the position. This is only shown for taxable accounts and only applies for orders where iRebal can transmit specific lot instructions. This excludes mutual fund sales done in dollars as well as positions in average cost accounts.</p>
          `
    }

    sortShares(valueA: number, valueB: number, nodeA: any, nodeB: any) {
        if(['GROUP', 'OTHER', 'BOND'].indexOf(nodeA.data.secType) !== -1) {
            valueA = Infinity * -1;
        }
        
        if(['GROUP', 'OTHER', 'BOND'].indexOf(nodeB.data.secType) !== -1) {
            valueB = Infinity * -1;
        }
        
        return valueA - valueB;
    }

    sortTxnAmt(valueA: number, valueB: number, nodeA: any, nodeB: any) {
        if(this.pros.getSellType(nodeA.data.txnType)) {
            valueA = valueA * -1; 
        }

        if(this.pros.getSellType(nodeB.data.txnType)) {
            valueB = valueB * -1; 
        }
        
        return valueA - valueB;
    }

    sortTxnShares(valueA: number, valueB: number, nodeA: any, nodeB: any) {
        if(['EQ', 'MF', 'OPTION', 'EXTERNAL'].indexOf(nodeA.data.secType) === -1) {
            valueA = Infinity * -1;
        } else if(this.pros.getSellType(nodeA.data.txnType)) {
            valueA = valueA * -1; 
        }
        
        if(['EQ', 'MF', 'OPTION', 'EXTERNAL'].indexOf(nodeB.data.secType) === -1) {
            valueB = Infinity * -1;
        } else if(this.pros.getSellType(nodeB.data.txnType)) {
            valueB = valueB * -1; 
        }
        
        return valueA - valueB;
    }

    preventLotSort(valueA: number, valueB: number, nodeA: any, nodeB: any, cbFunc: Function) {
        return (nodeA.data.parentId || nodeB.data.parentId) ? 0 : cbFunc(valueA, valueB, nodeA, nodeB);
    }

    getSharesPlaces(parent: ComPrOrdersComponent, params: any) {
        if(parent.ENABLE_FRACTIONAL_SHARES) {
            return (params.data?.secType === "OPTION") ? 0 : 4;
        } else {
            return (params.data?.secType === "MF") ? 4 : 0;
        }
    }

    getSharesInvalidMessage(parent: ComPrOrdersComponent, params: any) {
        if(parent.ENABLE_FRACTIONAL_SHARES) {
            return (params.data?.secType === "OPTION") ? 'Please enter only whole numbers.' : 'Please enter up to 4 decimal places.';
        } else {
            return (params.data?.secType === "MF") ? 'Please enter up to 4 decimal places.' : 'Please enter only whole numbers.';
        }
    }
}
